/** 更新与兼容文章：每篇正文只放 2 条不重复的内链 */
export const UPDATES_POSTS = {
	'gb-whatsapp-update-failed': {
		slug: 'gb-whatsapp-update-failed',
		categoryId: 'updates-compatibility',
		title: 'How to Fix GB WhatsApp Update Problems',
		description:
			'GB WhatsApp Pro update will not install. Separate a bad file, a signature conflict, and low storage before you uninstall the working copy.',
		keywords: 'gb whatsapp update failed',
		h1: 'GB WhatsApp Update Failed',
		lead:
			'The update failed if Android stopped during install. If the new version opens and then misbehaves, that is a different problem from an installer error.',
		body: `
      <h2>What failed</h2>
      <p>Compare the version in GB WhatsApp Pro → Settings → About with the version you just downloaded. Trust the About screen, not the filename. “Problem parsing the package” or “package appears to be invalid” usually means the file was cut off. Delete it and download again. Do not rename .apk to something else.</p>
      <p>A signature conflict means this APK is not an update to the copy on the phone. It is a different package. Android will not overwrite it. Uninstalling is the only way to switch, and uninstalling removes local chats unless you already have a backup.</p>

      <h2>Before you uninstall the version that still opens</h2>
      <ol class="article-steps">
        <li><strong>Leave at least 1 GB free</strong> so the installer can unpack the file.</li>
        <li><strong>Charge the phone or keep it plugged in.</strong> A shutdown halfway through can leave a half-installed app.</li>
        <li><strong>Back up while the old version still opens.</strong> Do this before you remove it to clear a signature clash.</li>
      </ol>
      <p>If the in-app backup button errors or never finishes, stop the uninstall. Fix that first with <a href="/gb-whatsapp-backup-not-working/">a GB WhatsApp backup that fails or never finishes</a>. A failed update does not delete chats by itself. Removing the app does.</p>

      <h2>Expired screen is not an install error</h2>
      <p>If the app opens and then says the version is too old, the APK you have is already installed. You do not need to fight the installer. That screen is <a href="/gb-whatsapp-expired-version/">an expired GB WhatsApp version that will not connect</a>. Update from a file you can match by size, after the backup above is done.</p>
    `,
		faqs: [
			['Does a failed update mean the number is banned?', 'No. An installer error happens before the new version runs. A ban is a message inside the app about the account.'],
		],
		publishedAt: '2026-10-23',
		updatedAt: '2026-11-04',
		coverTheme: 'update',
		coverLabel: 'Update Failed',
	},
	'gb-whatsapp-expired-version': {
		slug: 'gb-whatsapp-expired-version',
		categoryId: 'updates-compatibility',
		title: 'How to Fix the GB WhatsApp Expired Version Error',
		description:
			'GB WhatsApp Pro says this version has expired. Check the installed build, back up, and tell an expiry screen apart from a ban.',
		keywords: 'gb whatsapp expired version',
		h1: 'GB WhatsApp Expired Version',
		lead:
			'An expired-version screen means this build is too old for current servers. It is not the same as a ban timer on your number.',
		body: `
      <h2>Confirm it is expiry</h2>
      <p>Expiry usually blocks the outdated app and may still let you open backup or About. A ban logs you out, shows a countdown, or tells you to use official WhatsApp. If you can still read a version number, write it down before you install anything new.</p>
      <p>Open Settings → About if the app allows it. If the app closes before About opens, use the version you remember and the APK filename still in Downloads. <a href="/update/check-version/">How to read the GB WhatsApp version when About will not open</a> is the fallback when the screen closes immediately.</p>

      <h2>What to do next</h2>
      <ol class="article-steps">
        <li><strong>Back up or export</strong> while any menu still works. An expired build can lose that menu after you force an update.</li>
        <li><strong>Install a current build of the same app</strong>, not a random fork with a similar icon.</li>
        <li><strong>If Android refuses the new file</strong>, stop and fix the installer error. Do not delete the old app first.</li>
      </ol>
      <p>When the newer file will not install over the expired one, that is <a href="/gb-whatsapp-update-failed/">a GB WhatsApp update that Android will not install</a>. Signature clashes and broken downloads are why. Keep the expired app until a backup exists.</p>

      <h2>Do not treat it as a ban</h2>
      <p>Downloading another “expired fix” from an unknown page does not refresh the number. If the screen is a logout that demands the official app, you are not looking at an old version number. Stop requesting SMS codes and follow the wording on that banner.</p>
    `,
		faqs: [
			['Is expiry the same as a ban?', 'No. Expiry blocks an old app build. A ban restricts the phone number, often with a timer or an official-app warning.'],
		],
		publishedAt: '2026-10-24',
		updatedAt: '2026-11-04',
		coverTheme: 'update',
		coverLabel: 'Expired Version',
	},
	'gb-whatsapp-not-working-after-update': {
		slug: 'gb-whatsapp-not-working-after-update',
		categoryId: 'updates-compatibility',
		title: 'Why Is GB WhatsApp Not Working After Updating?',
		description:
			'GB WhatsApp Pro installed, then will not open, will not send, or lost a menu. Match the symptom before you reinstall.',
		keywords: 'gb whatsapp not working after update',
		h1: 'GB WhatsApp Not Working After an Update',
		lead:
			'If the update installed and the trouble started after that, name the symptom first: it will not open, messages sit on the clock, or a menu disappeared.',
		body: `
      <h2>It will not open</h2>
      <p>A crash on the icon, or a flash of the home screen and a close, is a crash loop. Restart the phone once. If it still closes, undo the last theme or font if you can reach settings for a second. Do not clear data yet.</p>
      <p>The step-by-step for that loop is <a href="/gb-whatsapp-keeps-crashing/">GB WhatsApp Pro that keeps closing after you open it</a>. Roll back to the previous build only after you know whether a backup exists.</p>

      <h2>It opens, but messages do not move</h2>
      <p>Chats that load while new texts stay on the clock are a send problem, not a failed install. Check date and time, turn off any VPN, and try mobile data. If that started the same day as the update, follow <a href="/gb-whatsapp-messages-not-sending/">messages stuck on the clock in GB WhatsApp Pro</a> before you uninstall.</p>
      <p>A login loop that keeps asking for a code is different again. Read the banner. If it says to use official WhatsApp, more SMS codes will not finish the update.</p>

      <h2>A menu you used is gone</h2>
      <p>Some releases move GB settings under a renamed item or drop an experimental switch. Search settings for “GB” before you assume the feature was deleted. If you installed official WhatsApp by mistake, the mod menus will be absent because that is a different app. Confirm the name on the About screen matches the build you meant to update.</p>
    `,
		faqs: [
			['Should I go back to an old APK?', 'Only if this build will not stay open and you still have the previous file. Very old files can show an expired-version screen.'],
		],
		publishedAt: '2026-10-25',
		updatedAt: '2026-11-04',
		coverTheme: 'update',
		coverLabel: 'After Update',
	},
	'gb-whatsapp-android-compatibility': {
		slug: 'gb-whatsapp-android-compatibility',
		categoryId: 'updates-compatibility',
		title: 'How to Check If Your Android Phone Supports GB WhatsApp',
		description:
			'Check Android version, free space, and 32-bit versus 64-bit before a GB WhatsApp Pro APK fails with “app not installed”.',
		keywords: 'gb whatsapp android compatibility',
		h1: 'GB WhatsApp Android Compatibility Issues',
		lead:
			'“Works on Android” is not enough. The APK you downloaded has its own minimum version, and some phones only run 64-bit packages.',
		body: `
      <h2>Check the phone before you install</h2>
      <ol class="article-steps">
        <li><strong>Open Settings → About phone</strong> and note the Android version.</li>
        <li><strong>Leave about 1 GB free</strong> on internal storage. A full disk often shows up as “app not installed” with no other explanation.</li>
        <li><strong>Read the APK notes for that file</strong>, not a generic “Android 5 or higher” line from an old page. Newer GB WhatsApp Pro builds drop old Android versions.</li>
      </ol>
      <p>If About says Android 8 or older, a current build may refuse to install even though an older GB copy still runs. Do not uninstall the working copy until you know the new file is for your version.</p>

      <h2>When Android rejects the file</h2>
      <p>A parse error or “app not installed” on a phone that meets the version can still be a bad download or a 32-bit package. Delete the file, download it again, and retry once. If the same message returns, the package does not match this phone. The message-by-message checks are in <a href="/gb-whatsapp-installation-error/">GB WhatsApp installation errors and what each Android message means</a>.</p>
      <p>Custom ROMs without the usual Google components can fail after install even when the APK parses. A Wi‑Fi-only tablet often cannot receive the SMS code at all, which looks like a compatibility bug and is actually verification.</p>

      <h2>If this phone is not supported</h2>
      <p>Use a build that lists your Android version, or stay on official WhatsApp. Forcing an incompatible APK by clearing data does not add missing system libraries. If your version is listed and the installer still blocks unknown apps, the remaining steps are in <a href="/installation/android/">allowing a GB WhatsApp APK to install on Android</a>.</p>
    `,
		faqs: [
			['Will a new phone always run it?', 'Only if that phone’s Android version and CPU type match the APK. A newer phone can still reject a 32-bit-only file.'],
		],
		publishedAt: '2026-10-26',
		updatedAt: '2026-11-04',
		coverTheme: 'update',
		coverLabel: 'Compatibility',
	},
}
